import { useEffect, useRef, useState } from 'react';
import { marcarArraste } from '../../utils/tempoReal';

const mesmoJogador = (a, b) => a && b && a.lista === b.lista && a.user_name === b.user_name;

// Jogador (cartão com data-drop) sob o ponto da tela, desde que não seja o próprio arrastado
function alvoEm(x, y, origem) {
  const el = document.elementFromPoint(x, y);
  const drop = el && el.closest('[data-drop]');
  if (!drop) return null;
  const [lista, ...nome] = drop.dataset.drop.split('|');
  const alvo = { lista, user_name: nome.join('|') };
  return mesmoJogador(alvo, origem) ? null : alvo;
}

/**
 * Arrastar e soltar jogadores entre os times e a fila (mouse e toque).
 * Cada cartão que aceita o drop tem `data-drop="<lista>|<user_name>"` (lista: time0, time1, espera);
 * a alça chama `iniciar(evento, lista, jogador)`. Ao soltar sobre outro jogador chama `onSoltar(origem, alvo)`.
 */
export default function useArrastarJogadores(onSoltar) {
  const [arraste, setArraste] = useState(null); // { origem, alvo, nome, x, y }
  const atual = useRef(null);
  atual.current = arraste;
  const arrastando = Boolean(arraste);

  // Tempo real: segura as atualizações enquanto o card está sendo arrastado
  useEffect(() => {
    marcarArraste(arrastando);
    return () => { if (arrastando) marcarArraste(false); };
  }, [arrastando]);

  useEffect(() => {
    if (!arrastando) return undefined;
    // Rola a página sozinha quando o dedo/mouse chega perto da borda (times em cima, fila embaixo)
    const BORDA = 90;
    let velocidade = 0;
    const rolagem = setInterval(() => {
      if (velocidade) window.scrollBy(0, velocidade);
    }, 16);
    const mover = (e) => {
      if (e.clientY < BORDA) velocidade = -Math.ceil((BORDA - e.clientY) / 6);
      else if (e.clientY > window.innerHeight - BORDA) velocidade = Math.ceil((e.clientY - (window.innerHeight - BORDA)) / 6);
      else velocidade = 0;
      setArraste((a) => a && { ...a, x: e.clientX, y: e.clientY, alvo: alvoEm(e.clientX, e.clientY, a.origem) });
    };
    const soltar = (e) => {
      const a = atual.current;
      setArraste(null);
      // Recalcula no ponto em que soltou: a página pode ter rolado sozinha com o dedo parado
      const alvo = a && alvoEm(e.clientX, e.clientY, a.origem);
      if (alvo) onSoltar(a.origem, alvo);
    };
    const cancelar = () => setArraste(null);
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', cancelar);
    return () => {
      clearInterval(rolagem);
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', cancelar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrastando]);

  const iniciar = (e, lista, jogador) => {
    e.preventDefault();
    setArraste({ origem: { lista, user_name: jogador.user_name }, alvo: null, nome: jogador.user_name, x: e.clientX, y: e.clientY });
  };

  // Estado visual de um cartão: origem (sendo arrastado) ou alvo (vai receber o drop)
  const estado = (lista, jogador) => ({
    origem: mesmoJogador(arraste?.origem, { lista, user_name: jogador.user_name }),
    alvo: mesmoJogador(arraste?.alvo, { lista, user_name: jogador.user_name }),
  });

  return { arraste, iniciar, estado };
}
